import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHasAdminAccess } from '../../hooks/useRoleCheck';
import { Users, GitBranch, CreditCard, Building2, User, Lock, Palette } from 'lucide-react';
import TeamsManagement from '../teams/TeamsManagement';
import LeadDistributionSettings from './LeadDistributionSettings';
import SubscriptionWrapper from './SubscriptionWrapper';
import OrganizationSettings from './OrganizationSettings';
import UserProfileSettings from './UserProfileSettings';
import { CustomizationContainer } from '../Customization';
import Header from '../../components/header';
import logger from '../../utils/logger';
import '../../styles/designSystem.css';
import './settingsPage.scss';

type SettingsTab = 'teams' | 'distribution' | 'subscription' | 'organization' | 'profile' | 'customization';

const SettingsPage: React.FC = () => {
  // 🔐 Centralized admin access check (edit ADMIN_ROLES in src/utils/roleUtils.ts)
  const isSuperAdmin = useHasAdminAccess();

  // 🔗 Read tab from URL query parameter
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as SettingsTab | null;

  logger.log('🔐 SettingsPage role check:', { isSuperAdmin, tabFromUrl });

  // Default tab based on role - non-admins go to profile, admins go to teams
  const getDefaultTab = (): SettingsTab => {
    // If tab is specified in URL and user has permission, use it
    if (tabFromUrl) {
      const adminOnlyTabs: SettingsTab[] = ['organization', 'customization', 'teams', 'distribution', 'subscription'];
      if (!adminOnlyTabs.includes(tabFromUrl) || isSuperAdmin) {
        return tabFromUrl;
      }
    }
    // Otherwise use role-based default
    return isSuperAdmin ? 'teams' : 'profile';
  };

  const [activeTab, setActiveTab] = useState<SettingsTab>(getDefaultTab());

  // 🔄 Update active tab when URL changes
  useEffect(() => {
    if (tabFromUrl) {
      const adminOnlyTabs: SettingsTab[] = ['organization', 'customization', 'teams', 'distribution', 'subscription'];
      if (!adminOnlyTabs.includes(tabFromUrl) || isSuperAdmin) {
        setActiveTab(tabFromUrl);
      }
    }
  }, [tabFromUrl, isSuperAdmin]);

  // 🔐 Define all tabs with adminOnly flag
  const allTabs = [
    {
      id: 'organization' as SettingsTab,
      label: 'Organization',
      icon: Building2,
      description: 'Organization settings',
      adminOnly: true // 🔒 SUPER_ADMIN only
    },
    {
      id: 'customization' as SettingsTab,
      label: 'Customization',
      icon: Palette,
      description: 'Customize CRM for your needs',
      adminOnly: true // 🔒 SUPER_ADMIN only
    },
    {
      id: 'profile' as SettingsTab,
      label: 'User Profile',
      icon: User,
      description: 'Personal information',
      adminOnly: false
    },
    {
      id: 'teams' as SettingsTab,
      label: 'Teams',
      icon: Users,
      description: 'Manage team members and roles',
      adminOnly: true // 🔒 SUPER_ADMIN only
    },
    {
      id: 'distribution' as SettingsTab,
      label: 'Lead Distribution',
      icon: GitBranch,
      description: 'Configure distribution rules',
      adminOnly: true // 🔒 SUPER_ADMIN only
    },
    {
      id: 'subscription' as SettingsTab,
      label: 'Subscription',
      icon: CreditCard,
      description: 'Plans and billing',
      adminOnly: true // 🔒 SUPER_ADMIN only
    }
  ];

  // 🔐 Filter tabs based on user role
  const tabs = useMemo(() => {
    return allTabs.filter(tab => !tab.adminOnly || isSuperAdmin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]); // allTabs is stable (defined in component)

  // 🔐 Secure content rendering - also validates role server-side
  const renderContent = () => {
    // Security check: If non-admin tries to access admin tabs (e.g., via URL or dev tools)
    const adminOnlyTabs: SettingsTab[] = ['organization', 'customization', 'teams', 'distribution', 'subscription'];
    if (adminOnlyTabs.includes(activeTab) && !isSuperAdmin) {
      logger.log('🚫 Unauthorized access attempt to admin tab:', activeTab);
      return (
        <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{
          height: '400px',
          textAlign: 'center',
          padding: '40px'
        }}>
          <Lock size={48} color="#dc3545" style={{ marginBottom: '16px' }} />
          <h3 className="ds-h4" style={{ color: '#dc3545', marginBottom: '8px' }}>
            Access Denied
          </h3>
          <p className="ds-body ds-text-secondary">
            You don't have permission to access this section.
            <br />
            Please contact your administrator.
          </p>
        </div>
      );
    }

    switch (activeTab) {
      case 'organization':
        return <OrganizationSettings />;
      case 'customization':
        return <CustomizationContainer />;
      case 'profile':
        return <UserProfileSettings />;
      case 'teams':
        return <TeamsManagement />;
      case 'distribution':
        return <LeadDistributionSettings />;
      case 'subscription':
        return <SubscriptionWrapper />;
      default:
        return <UserProfileSettings />; // Safe default for all users
    }
  };

  return (
    <>
      <Header />
      <div className="settings-page-container">
        {/* Settings Sidebar Navigation */}
        <aside className="settings-sidebar-nav">
          <div className="settings-sidebar-header">
            <div className="ds-flex ds-items-center ds-gap-3">
              <h2 className="ds-h3" style={{ margin: 0 }}>Settings</h2>
           
            </div>
            <p className="ds-body-sm ds-text-secondary" style={{ margin: '0.5rem 0 0 0' }}>
              Customize 3Sigma settings
            </p>
          </div>

          <nav className="settings-nav-list">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`settings-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <div className="settings-nav-item-icon">
                    <Icon size={20} />
                  </div>
                  <div className="settings-nav-item-content">
                    <div className="settings-nav-item-label">{tab.label}</div>
                    <div className="settings-nav-item-description">{tab.description}</div>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Settings Content */}
        <main className="settings-main-content">
          {renderContent()}
        </main>
      </div>
    </>
  );
};

export default SettingsPage;